import React, { useEffect, useState } from "react";
import { formatMoney, useGetPaymentCurrencyList } from "../../utils";
import { EmptyPieChart } from "./PieChart/Empty";
import { PieChart, Pie, Tooltip } from "recharts";
import { useCompanyStore } from "../../state/useCompanyStore";
import ExpenseEmptyStatIcon from "../../assets/icons/ExpenseEmptyStatIcon";
import ChartLoader from "./Loading/ChartLoader";

interface PieData {
  value: number;
  fill: string;
}

interface PieChartProps {
  title?: string;
  isLoading?: boolean;
  data: Record<string, PieData> | any;
  toolTipText?: string;
  isFilter: boolean;
}

const ExpenseCategories: React.FC<PieChartProps> = ({
  title,
  data = {},
  isLoading,
  isFilter,
}) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );

  useEffect(() => {
    const transformedData = Object.keys(data)
      .filter((key) => data[key].fill && data[key].value !== null)
      .map((key) => ({
        name: key,
        fill: data[key].fill,
        value: data[key].value,
        amount: data[key].amount,
      }));
    setChartData(transformedData);
  }, [data]);

  const renderEmptyComponent = () => {
    return (
      <div className="pb-2 pt-0 bg-white rounded-lg w-full">
        <EmptyPieChart
          link={`/expenses/create`}
          buttonText="Create an expense"
          paragraph={
            <>
              Create your expense to start <br /> recording payments.
            </>
          }
          title="Expense Categories"
          icon={<ExpenseEmptyStatIcon />}
        />
      </div>
    );
  };

  const renderEmptyFilteredComponent = () => {
    return (
      <>
        <div className="py-4 px-5">
          <p className={`font-semibold text-[16px] text-[##000000] leading-6`}>
            {title}
          </p>
        </div>
        <hr className="border border-[#EAECF0] mt-1 w-full" />
        <div className=" pt-0 bg-white rounded-lg h-[285px] pb-10 flex justify-center items-center">
          <h1 className="text-medium text-[15px] text-[##000000] leading-6">
            No data available for this date range.
          </h1>
        </div>
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <ChartLoader />
      ) : (
        <>
          {chartData.length == 0 ? (
            <>
              {isFilter
                ? renderEmptyFilteredComponent()
                : renderEmptyComponent()}
            </>
          ) : (
            <div>
              {chartData.length == 0 || (
                <>
                  <div className="py-4 px-5">
                    <p
                      className={`font-semibold text-[16px] text-[##000000] leading-6`}
                    >
                      {title}
                    </p>
                  </div>
                  <hr className="border border-[#EAECF0] mt-1 w-full" />
                </>
              )}
              <div
                className={`flex flex-col items-center justify-center px-6 pb-5 w-full h-full ${chartData.length == 0 && "mt-10"}`}
              >
                {chartData.length > 0 ? (
                  <PieChart width={220} height={200}>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={85}
                      paddingAngle={0}
                      style={{ outline: "none" }}
                    />
                    <Tooltip
                      formatter={(value: number, name: string, entry: any) =>
                        `${formatMoney(entry.payload.amount, userCurrencyCode.currency)}`
                      }
                      contentStyle={{
                        fontSize: "12px",
                        padding: "5px",
                        backgroundColor: "#FFFFFF",
                        border: "none",
                        borderRadius: "8px",
                      }}
                    />
                  </PieChart>
                ) : (
                  <EmptyPieChart
                    link={`/expenses/create`}
                    buttonText="Create an Expense"
                    paragraph={`Create your expense to start recording transactions`}
                  />
                )}

                <div className="flex flex-wrap gap-3 justify-center">
                  {chartData &&
                    chartData.map((item, i) => (
                      <div
                        className="flex flex-row gap-1 items-center justify-between"
                        style={{ whiteSpace: "nowrap" }}
                        key={i}
                      >
                        <div
                          className="rounded-lg w-[8px] h-[8px]"
                          style={{ backgroundColor: item.fill }}
                        ></div>
                        <p className="text-[14px] leading-4 font-medium text-[#344054]">
                          <span>{item.name}</span> <span> - {item.value}%</span>
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export { ExpenseCategories };
